/**
 * CustomerOrdersPage Component
 * Main page for customer order tracking
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-007
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { RefreshCw, Package, TrendingUp } from 'lucide-react';
import { CustomerOrdersList } from './CustomerOrdersList';
import { OrderFiltersBar } from './OrderFiltersBar';
import { FilterDrawer } from './FilterDrawer';
import { useCustomerOrders, useOrderFilters } from '../hooks';
import type { PaginationParams } from '../types/order.types';

// ============================================
// COMPONENT
// ============================================

/**
 * CustomerOrdersPage Component
 * Main container for customer order tracking with filters and pagination
 *
 * @example
 * ```tsx
 * <CustomerOrdersPage />
 * ```
 */
export function CustomerOrdersPage() {
  // State management
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 0,
    size: 20,
  });
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  // Filter state
  const {
    filters,
    setSearchTerm,
    toggleStatus,
    clearFilters,
    hasActiveFilters,
    activeFilterCount,
  } = useOrderFilters();

  // Fetch orders with React Query
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useCustomerOrders({
    pagination,
    filters,
  });

  // Event handlers
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewDetails = (orderId: number) => {
    // Navigate to order details page
    console.log('[CustomerOrdersPage] View order details:', orderId);
    // TODO: Implement navigation to order details page
  };

  const handleRefresh = () => {
    refetch();
  };

  // Statistics from data
  const stats = data ? {
    totalOrders: data.totalElements,
    activeOrders: data.orders.filter(o =>
      !['DELIVERED', 'CANCELLED'].includes(o.orderStatus)
    ).length,
  } : { totalOrders: 0, activeOrders: 0 };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Background decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400 opacity-5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-400 opacity-5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 container mx-auto py-8 px-4 sm:px-6 lg:px-8 space-y-8 max-w-7xl">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        >
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">
              My Orders
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Track your order status and delivery information
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </motion.div>

        {/* Statistics Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
        >
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="rounded-full bg-blue-100 p-3">
                  <Package className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-600">Total Orders</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {stats.totalOrders}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="rounded-full bg-green-100 p-3">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-600">Active Orders</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {stats.activeOrders}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Filters Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <OrderFiltersBar
            filters={filters}
            onSearchChange={setSearchTerm}
            onStatusToggle={toggleStatus}
            onClearFilters={clearFilters}
            onOpenMobileFilters={() => setIsMobileFilterOpen(true)}
            activeFilterCount={activeFilterCount}
          />
        </motion.div>

        {/* Orders List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <CustomerOrdersList
            data={data}
            isLoading={isLoading}
            isError={isError}
            error={error}
            onViewDetails={handleViewDetails}
            onPageChange={handlePageChange}
            currentPage={pagination.page}
          />
        </motion.div>

        {/* Mobile Filter Drawer */}
        <FilterDrawer
          open={isMobileFilterOpen}
          onOpenChange={setIsMobileFilterOpen}
          filters={filters}
          onStatusToggle={toggleStatus}
          onApplyFilters={() => setIsMobileFilterOpen(false)}
          onClearFilters={clearFilters}
        />
      </div>
    </div>
  );
}

export default CustomerOrdersPage;
